import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  imagesAdded,
  imageRemoved,
  imagesReordered,
  pageSizeChanged,
  pageOrientationChanged,
  imageQualityChanged,
  marginChanged,
  fitToPageChanged,
  clearError,
  markAsDownloaded,
  resetConverter,
  generatePdfAsync,
  validateImageFile,
  generateUniqueId,
  selectState,
  PageSize,
  PageOrientation,
  ImageQuality,
  ImageFile,
} from "./ImageToPdfConverterSlice";
import {
  Box,
  Divider,
  MenuItem,
  Stack,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  Switch,
  FormControlLabel,
  Slider,
  LinearProgress,
} from "@mui/material";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { HomeCard } from "../../components/home_card/HomeCard";
import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import BarcodeGeneratorImage from "../../assets/BarcodeGenerator.webp";
import { useState, useCallback, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function ImageToPdfConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { t } = useTranslation();

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files) return;

    const newImages: ImageFile[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const validationError = validateImageFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        return;
      }

      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const imageFile: ImageFile = {
          id: generateUniqueId(),
          file,
          dataUrl: reader.result as string,
          name: file.name,
          size: file.size,
          type: file.type,
        };
        newImages.push(imageFile);
        
        if (newImages.length === Array.from(files).length - errors.length) {
          dispatch(imagesAdded(newImages));
        }
      });
      reader.readAsDataURL(file);
    });

    if (errors.length > 0) {
      dispatch(clearError());
      // You could show these errors in a more user-friendly way
      console.error("File validation errors:", errors);
    }
  }, [dispatch]);

  const handleFileDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleFileDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleImageDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
  }, []);

  const handleImageDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const handleImageDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
    
    if (dragIndex === dropIndex) return;

    const items = Array.from(state.images);
    const [reorderedItem] = items.splice(dragIndex, 1);
    items.splice(dropIndex, 0, reorderedItem);

    dispatch(imagesReordered(items));
  }, [state.images, dispatch]);

  const handleConvertToPdf = useCallback(() => {
    dispatch(generatePdfAsync());
  }, [dispatch]);

  const handleReset = useCallback(() => {
    dispatch(resetConverter());
  }, [dispatch]);

  // Auto-download PDF when generated
  useEffect(() => {
    if (state.generatedPdf && state.pdfFileName && !state.hasDownloaded) {
      const link = document.createElement("a");
      link.href = state.generatedPdf;
      link.download = state.pdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      dispatch(markAsDownloaded());
    }
  }, [state.generatedPdf, state.pdfFileName, state.hasDownloaded, dispatch]);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("imageToPdfConverter.title")}
      pageSeoTitle={t("imageToPdfConverter.seoTitle")}
      pageDescription={t("imageToPdfConverter.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 900, lg: 1200 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("imageToPdfConverter.mainHeading")}
            </Typography>
          </Grid2>

          {/* File Upload Section */}
          <Grid2 xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 2, background: "#2e303200", borderColor: "#282B39", borderStyle: "dashed" }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("imageToPdfConverter.uploadImages")}
              </Typography>

              <Box
                sx={{
                  border: `2px dashed ${isDragOver ? "#FFD9A8" : "#7895a1"}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: "center",
                  backgroundColor: isDragOver ? "rgba(255, 217, 168, 0.1)" : "transparent",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  mb: 2,
                }}
                                 onDragOver={handleFileDragOver}
                 onDragLeave={handleFileDragLeave}
                 onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <UploadIcon sx={{ fontSize: 48, color: "#7895a1", mb: 2 }} />
                <Typography variant="h6" color={"secondary"} sx={{ mb: 1 }}>
                  {t("imageToPdfConverter.dropImagesHere")}
                </Typography>
                <Typography variant="body2" color="info.main">
                  {t("imageToPdfConverter.supportedFormats")}
                </Typography>
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => handleFileSelect(e.target.files)}
                style={{ display: "none" }}
              />

              {state.images.length > 0 && (
                <Box>
                  <Typography variant="h6" color={"#FFFFFF"} sx={{ mb: 2 }}>
                    {t("imageToPdfConverter.selectedImages")} ({state.images.length})
                  </Typography>
                  
                                                        <Stack spacing={1}>
                     {state.images.map((image, index) => (
                       <Paper
                         key={image.id}
                         draggable
                         onDragStart={(e) => handleImageDragStart(e, index)}
                         onDragOver={handleImageDragOver}
                         onDrop={(e) => handleImageDrop(e, index)}
                         sx={{
                           p: 2,
                           display: "flex",
                           alignItems: "center",
                           gap: 2,
                           backgroundColor: "#2e3032",
                           cursor: "grab",
                           "&:active": {
                             cursor: "grabbing",
                           },
                         }}
                       >
                         <DragIndicatorIcon sx={{ color: "#7895a1" }} />
                         
                         <img
                           src={image.dataUrl}
                           alt={image.name}
                           style={{
                             width: 40,
                             height: 40,
                             objectFit: "cover",
                             borderRadius: 4,
                           }}
                         />
                         
                         <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                           <Typography variant="body2" noWrap>
                             {image.name}
                           </Typography>
                           <Typography variant="caption" color="info.main">
                             {formatFileSize(image.size)}
                           </Typography>
                         </Box>
                         
                         <Tooltip title={t("imageToPdfConverter.remove")}>
                           <IconButton
                             onClick={() => dispatch(imageRemoved(image.id))}
                             color="secondary"
                             size="small"
                           >
                             <DeleteIcon />
                           </IconButton>
                         </Tooltip>
                       </Paper>
                     ))}
                   </Stack>
                  
                  <Typography variant="caption" color="info.main" sx={{ mt: 1, display: "block" }}>
                    {t("imageToPdfConverter.dragToReorder")}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid2>

          {/* Settings Section */}
          <Grid2 xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 2, background: "#2e303200", borderColor: "#282B39", borderStyle: "dashed" }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t("imageToPdfConverter.pdfSettings")}
              </Typography>

              <Stack spacing={3}>
                <CustomSelect
                  label={t("imageToPdfConverter.pageSize")}
                  value={state.pageSize}
                  onChange={(e) => dispatch(pageSizeChanged(e.target.value as PageSize))}
                >
                  <MenuItem value={PageSize.A4}>A4 (210 × 297 mm)</MenuItem>
                  <MenuItem value={PageSize.LETTER}>Letter (8.5 × 11 in)</MenuItem>
                  <MenuItem value={PageSize.LEGAL}>Legal (8.5 × 14 in)</MenuItem>
                  <MenuItem value={PageSize.A3}>A3 (297 × 420 mm)</MenuItem>
                  <MenuItem value={PageSize.A5}>A5 (148 × 210 mm)</MenuItem>
                </CustomSelect>

                <CustomSelect
                  label={t("imageToPdfConverter.pageOrientation")}
                  value={state.pageOrientation}
                  onChange={(e) => dispatch(pageOrientationChanged(e.target.value as PageOrientation))}
                >
                  <MenuItem value={PageOrientation.PORTRAIT}>{t("imageToPdfConverter.portrait")}</MenuItem>
                  <MenuItem value={PageOrientation.LANDSCAPE}>{t("imageToPdfConverter.landscape")}</MenuItem>
                </CustomSelect>

                <CustomSelect
                  label={t("imageToPdfConverter.imageQuality")}
                  value={state.imageQuality}
                  onChange={(e) => dispatch(imageQualityChanged(e.target.value as ImageQuality))}
                >
                  <MenuItem value={ImageQuality.LOW}>{t("imageToPdfConverter.low")}</MenuItem>
                  <MenuItem value={ImageQuality.MEDIUM}>{t("imageToPdfConverter.medium")}</MenuItem>
                  <MenuItem value={ImageQuality.HIGH}>{t("imageToPdfConverter.high")}</MenuItem>
                </CustomSelect>

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t("imageToPdfConverter.margin")}: {state.margin}mm
                  </Typography>
                  <Slider
                    value={state.margin}
                    onChange={(_, value) => dispatch(marginChanged(value as number))}
                    min={0}
                    max={50}
                    step={5}
                    marks
                    valueLabelDisplay="auto"
                    sx={{ color: "#FFD9A8" }}
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={state.fitToPage}
                      onChange={(e) => dispatch(fitToPageChanged(e.target.checked))}
                      color="primary"
                    />
                  }
                  label={t("imageToPdfConverter.fitToPage")}
                />
              </Stack>
            </Paper>
          </Grid2>

          {/* Action Buttons */}
          <Grid2 xs={12}>
            <Stack direction="row" spacing={2}>
              <CustomButton
                icon={<UploadIcon sx={{ color: "#FFD9A8" }} />}
                label={state.isConverting ? t("imageToPdfConverter.converting") : t("imageToPdfConverter.convertToPdf")}
                onClick={handleConvertToPdf}
                disabled={state.isConverting || state.images.length === 0}
              />

              <Tooltip title={t("imageToPdfConverter.reset")}>
                <IconButton onClick={handleReset} color="secondary">
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid2>

          {/* Progress Bar */}
          {state.isConverting && (
            <Grid2 xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t("imageToPdfConverter.convertingProgress")} {state.progress}%
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={state.progress}
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: "#2e3032",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#FFD9A8",
                    },
                  }}
                />
              </Box>
            </Grid2>
          )}

          {/* Error Display */}
          {state.error && (
            <Grid2 xs={12}>
              <Alert
                severity="error"
                onClose={() => dispatch(clearError())}
                sx={{ mb: 2 }}
              >
                {state.error}
              </Alert>
            </Grid2>
          )}



          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>

          {/* SEO Content */}
          <Grid2 xs={12}>
            <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageToPdfConverter.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageToPdfConverter.content.freeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageToPdfConverter.content.multipleImagesHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageToPdfConverter.content.multipleImagesDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageToPdfConverter.content.customizableHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageToPdfConverter.content.customizableDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("imageToPdfConverter.content.easyToUseHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("imageToPdfConverter.content.easyToUseDescription")}
            </Typography>
          </Grid2>

          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("imageToPdfConverter.related")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="qrCodeGenerator"
              link="/QrCodeGenerator"
              smallCard={true}
              image={QrCodeGeneratorImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="barcodeGenerator"
              link="/BarcodeGenerator"
              smallCard={true}
              image={BarcodeGeneratorImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
